import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { AgremoStatisticsComponent } from '../../../shared/components/agremo-statistics/agremo-statistics.component';
import { AgremoStatistics } from '../../../core/models/agremo-analysis.model';

/**
 * Agremo Map Analysis preview (Task Group 10, FR-6.1)
 *
 * Route: /analyses/preview — no MembershipTierGuard here (this IS the
 * Core-reachable landing spot the guard on /analyses redirects to; guarding
 * it too would be a redirect loop). `sampleStatistics` is fabricated display
 * data, not a real analysis — it only exists to show a Core org what the
 * output looks like, matching FR-6.1's "sample imagery/statistics" ask via
 * the same `AgremoStatisticsComponent` the real `/analyses/:id/view` page
 * renders once an org is Pro.
 *
 * Reference: agent-os/specs/2026-06-29-core-pro-membership/spec.md FR-6
 */
@Component({
  selector: 'app-agremo-preview',
  standalone: true,
  imports: [CommonModule, RouterLink, AgremoStatisticsComponent],
  template: `
    <div class="d-flex justify-content-between align-items-start mb-4">
      <div>
        <h1 class="h2 mb-1 fw-bold">Field Analyses</h1>
        <p class="text-muted mb-0">Advanced crop analysis from drone imagery</p>
      </div>
    </div>

    <div class="card mb-4">
      <div class="card-body p-4 p-md-5 text-center">
        <i class="bi bi-graph-up-arrow hero-icon"></i>
        <h4 class="fw-bold mt-3 mb-2">Agremo Map Analysis is a Pro feature</h4>
        <p class="text-muted mb-4 mx-auto mw-520px">
          Request, run, and view crop-health analyses straight from your drone imagery — see exactly
          which parts of a field are stressed before you ever leave the house. The sample below shows
          what a real result looks like.
        </p>
        <a routerLink="/billing/upgrade" class="btn btn-primary px-4">Upgrade to Pro</a>
      </div>
    </div>

    <div class="card">
      <div class="card-header d-flex align-items-center gap-2">
        <i class="bi bi-eye text-muted"></i>
        <span class="fw-semibold">Sample analysis</span>
        <span class="badge bg-secondary-subtle text-secondary ms-auto">Not real data</span>
      </div>
      <div class="card-body">
        <app-agremo-statistics [statistics]="sampleStatistics"></app-agremo-statistics>
      </div>
    </div>
  `,
})
export class AgremoPreviewComponent {
  readonly sampleStatistics: AgremoStatistics = {
    general: {
      general_area: 42.5,
      general_label: 'Total Field Area',
      general_area_unit: 'ac',
      general_percentage: 100,
      general_stress_area: 6.8,
    },
    clusters: [
      { id: 1, area: 35.7, color: '2ecc71', label: 'Healthy', area_unit: 'ac', percentage: 84 },
      { id: 2, area: 6.8, color: 'e74c3c', label: 'Stressed', area_unit: 'ac', percentage: 16 },
    ],
  };
}
